import isEqual from 'lodash/isEqual';
import axios, { CancelToken } from 'axios';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import isObject from 'lodash/isObject';
import { Deck, WebMercatorViewport, Layer } from '@deck.gl/core';
import { lngLatToWorld } from 'viewport-mercator-project';
import { CompositeLayer } from 'deck.gl';
import GL from '@luma.gl/constants';
import { Model, Geometry, Texture2D, fp64 } from 'luma.gl';
import { loadImage } from '@loaders.gl/images';
import sortBy from 'lodash/sortBy';

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var defineProperty = function (obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var get = function get(object, property, receiver) {
  if (object === null) object = Function.prototype;
  var desc = Object.getOwnPropertyDescriptor(object, property);

  if (desc === undefined) {
    var parent = Object.getPrototypeOf(object);

    if (parent === null) {
      return undefined;
    } else {
      return get(parent, property, receiver);
    }
  } else if ("value" in desc) {
    return desc.value;
  } else {
    var getter = desc.get;

    if (getter === undefined) {
      return undefined;
    }

    return getter.call(receiver);
  }
};

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

var handleCallback = function handleCallback(resolve, reject, callback, r) {
  try {
    resolve(callback(r));
  } catch (e) {
    reject(e);
  }
};

var CancelablePromise = function () {
  createClass(CancelablePromise, null, [{
    key: "all",
    value: function all(iterable) {
      return new CancelablePromise(function (y, n) {
        Promise.all(iterable).then(y, n);
      });
    }
  }, {
    key: "race",
    value: function race(iterable) {
      return new CancelablePromise(function (y, n) {
        Promise.race(iterable).then(y, n);
      });
    }
  }, {
    key: "reject",
    value: function reject(value) {
      return new CancelablePromise(function (y, n) {
        Promise.reject(value).then(y, n);
      });
    }
  }, {
    key: "resolve",
    value: function resolve(value) {
      return new CancelablePromise(function (y, n) {
        Promise.resolve(value).then(y, n);
      });
    }
  }]);

  function CancelablePromise(executor) {
    classCallCheck(this, CancelablePromise);

    this._promise = new Promise(executor);
    this._canceled = false;
  }

  createClass(CancelablePromise, [{
    key: "then",
    value: function then(success, error) {
      var _this = this;

      var p = new CancelablePromise(function (resolve, reject) {
        _this._promise.then(function (r) {
          if (_this._canceled) {
            p.cancel();
          }
          if (success && !_this._canceled) {
            handleCallback(resolve, reject, success, r);
          } else {
            resolve(r);
          }
        }, function (r) {
          if (_this._canceled) {
            p.cancel();
          }
          if (error && !_this._canceled) {
            handleCallback(resolve, reject, error, r);
          } else {
            reject(r);
          }
        });
      });
      return p;
    }
  }, {
    key: "catch",
    value: function _catch(error) {
      return this.then(undefined, error);
    }
  }, {
    key: "cancel",
    value: function cancel(errorCallback) {
      this._canceled = true;
      if (errorCallback) {
        this._promise.catch(errorCallback);
      }
      return this;
    }
  }]);
  return CancelablePromise;
}();

var LayerModel = function () {
  function LayerModel() {
    var layerSpec = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    classCallCheck(this, LayerModel);
    this.opacity = 1;
    this.visibility = true;
    this.source = {};
    this.render = {};

    Object.assign(this, layerSpec, { changedAttributes: {} });
  }

  createClass(LayerModel, [{
    key: 'get',
    value: function get$$1(key) {
      return this[key];
    }
  }, {
    key: 'set',
    value: function set$$1(key, value) {
      this[key] = value;
      return this;
    }
  }, {
    key: 'update',
    value: function update(layerSpec) {
      var _this = this;

      var prevData = _extends({}, this);
      var nextData = _extends({}, layerSpec);

      // reseting changedAttributes for every update
      this.set('changedAttributes', {});

      Object.keys(nextData).forEach(function (k) {
        if (!isEqual(prevData[k], nextData[k])) {
          _this.changedAttributes[k] = nextData[k];
          _this.set(k, nextData[k]);
        }
      });
    }
  }]);
  return LayerModel;
}();

var headers = {
  'Content-Type': 'application/json'
};

var get$1 = function get$$1(url) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return axios.get(url, _extends({
    headers: headers
  }, options));
};

var post = function post(url, body) {
  return axios.post(url, body, { headers: headers });
};

var fetch$1 = function fetch(type, url, options, layerModel) {
  var layerRequest = layerModel.layerRequest;


  if (layerRequest) {
    layerRequest.cancel('Operation canceled by the user.');
  }

  var layerRequestSource = CancelToken.source();
  layerModel.set('layerRequest', layerRequestSource);

  var method = type === 'post' ? post : get$1;

  var newLayerRequest = method(url, _extends({}, options, {
    cancelToken: layerRequestSource.token
  })).then(function (res) {
    if (res.status > 400) {
      console.error(res);
      return false;
    }

    return res.data;
  });

  return newLayerRequest;
};

var DEFAULT_PROVIDERS = {
  carto: function carto(layerModel, layer, resolve, reject) {
    var interactivity = layerModel.interactivity,
        source = layerModel.source;
    var provider = source.provider;


    var layerTpl = JSON.stringify({
      version: '1.3.0',
      stat_tag: 'API',
      layers: provider.layers.map(function (l) {
        if (!!interactivity && interactivity.length) {
          return _extends({}, l, { options: _extends({}, l.options, { interactivity: interactivity }) });
        }
        return l;
      })
    });

    // https://carto.com/developers/auth-api/guides/how-to-send-API-Keys/
    var apiParams = _extends({
      stat_tag: 'API',
      config: encodeURIComponent(layerTpl)
    }, provider.api_key && { api_key: provider.api_key });
    var apiParamsString = Object.keys(apiParams).map(function (k) {
      return k + '=' + apiParams[k];
    }).join('&');
    var url = 'https://' + provider.account + '.carto.com/api/v1/map?' + apiParamsString;

    fetch$1('get', url, {}, layerModel).then(function (response) {
      var ext = layerModel.type === 'vector' ? 'mvt' : 'png';
      var tileUrl = response.cdn_url.templates.https.url.replace('{s}', 'a') + '/' + provider.account + '/api/v1/map/' + response.layergroupid + '/{z}/{x}/{y}.' + ext;

      return resolve(_extends({}, layer, {
        source: _extends({}, omit(layer.source, 'provider'), {
          tiles: [tileUrl]
        })
      }));
    }).catch(function (err) {
      return reject(err);
    });
  }
};

function checkPluginProperties(plugin) {
  if (plugin) {
    var requiredProperties = ['add', 'remove', 'setVisibility', 'setOpacity', 'setZIndex', 'setSource', 'setRender', 'setParams', 'setSQLParams', 'setDecodeParams', 'getLayerByType'];

    requiredProperties.forEach(function (property) {
      if (!plugin[property]) {
        console.error('The ' + property + ' function is required for layer manager plugins');
      }
    });

    var deprecatedProperties = ['getLayerByProvider'];
    deprecatedProperties.forEach(function (property) {
      if (plugin[property]) {
        console.warn('The ' + property + ' function is deprecated for layer manager plugins');
      }
    });
  }
}

var LayerManager = function () {
  function LayerManager(map, Plugin, Providers) {
    classCallCheck(this, LayerManager);

    this.map = map;
    this.providers = _extends({}, DEFAULT_PROVIDERS, Providers);
    this.layers = [];
    this.promises = {};
    this.options = {
      getLayers: this.getLayers.bind(this)
    };

    this.plugin = new Plugin(this.map, this.options);
    checkPluginProperties(this.plugin);
  }

  /**
   * Add layers
   * @param {Array} layers
   * @param {Object} layerOptions
   */


  createClass(LayerManager, [{
    key: 'add',
    value: function add(layer) {
      var layerOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        opacity: 1,
        visibility: true,
        zIndex: 0,
        interactivity: null
      };
      var onAfterAdd = arguments[2];

      if (typeof layer === 'undefined') {
        console.error('layer is required');
        return this;
      }

      var layerModel = new LayerModel(_extends({}, layer, layerOptions));
      var id = layerModel.id;


      if (this.layers.find(function (l) {
        return l.id === id;
      })) {
        return this.layers;
      }
      this.layers.push(layerModel);

      this.requestLayer(layerModel, onAfterAdd);

      return this.layers;
    }

    /**
     * Updating a specific layer
     * @param  {Object} layerModel
     */

  }, {
    key: 'update',
    value: function update(id, changedProps) {
      var layerModel = this.getLayerModel(id);
      if (!layerModel || !layerModel.mapLayer) return;

      layerModel.update(changedProps);

      var opacity = changedProps.opacity,
          visibility = changedProps.visibility,
          zIndex = changedProps.zIndex,
          source = changedProps.source,
          render = changedProps.render,
          params = changedProps.params,
          sqlParams = changedProps.sqlParams,
          decodeParams = changedProps.decodeParams;


      if (typeof opacity !== 'undefined') {
        this.plugin.setOpacity(layerModel, opacity);
      }

      if (typeof visibility !== 'undefined') {
        this.plugin.setVisibility(layerModel, visibility);
      }

      if (typeof zIndex !== 'undefined') {
        this.plugin.setZIndex(layerModel, zIndex);
      }

      if (!isEmpty(source)) {
        this.plugin.setSource(layerModel);
      }

      if (!isEmpty(render)) {
        this.plugin.setRender(layerModel);
      }

      if (!isEmpty(params)) {
        this.plugin.setParams(layerModel);
      }

      if (!isEmpty(sqlParams)) {
        this.plugin.setSqlParams(layerModel);
      }

      if (!isEmpty(decodeParams)) {
        this.plugin.setDecodeParams(layerModel);
      }
    }

    /**
     * Remove a layer giving a Layer ID
     * @param {String} id
     */

  }, {
    key: 'remove',
    value: function remove(id, onAfterRemove) {
      var layers = this.layers.slice(0);

      this.requestCancel(id);

      var layerModel = this.getLayerModel(id);

      if (layerModel) {
        this.plugin.remove(layerModel);
      }

      this.layers = layers.filter(function (l) {
        return l.id !== id;
      });

      onAfterRemove(layerModel);
    }
  }, {
    key: 'getLayers',
    value: function getLayers() {
      return this.layers;
    }
  }, {
    key: 'getLayerModel',
    value: function getLayerModel(id) {
      return this.layers.find(function (l) {
        return l.id === id;
      });
    }

    /**
     * A namespace to set opacity on selected layer
     * @param {String} id
     * @param {Number} opacity
     */

  }, {
    key: 'setOpacity',
    value: function setOpacity(id, opacity) {
      var layerModel = this.getLayerModel(id);
      this.plugin.setOpacity(layerModel, opacity);
    }

    /**
     * A namespace to hide or show a selected layer
     * @param {String} id
     * @param {Boolean} visibility
     */

  }, {
    key: 'setVisibility',
    value: function setVisibility(id, visibility) {
      var layerModel = this.getLayerModel(id);
      this.plugin.setVisibility(layerModel, visibility);
    }

    /**
     * A namespace to set z-index on selected layer
     * @param {String} id
     * @param {Number} zIndex
     */

  }, {
    key: 'setZIndex',
    value: function setZIndex(id, zIndex) {
      var layerModel = this.getLayerModel(id);
      this.plugin.setZIndex(layerModel, zIndex);
    }
  }, {
    key: 'requestLayer',
    value: function requestLayer(layerModel, onAfterAdd) {
      var _this = this;

      var type = layerModel.type;

      var method = this.plugin.getLayerByType(type);

      if (!method) {
        this.promises[layerModel.id] = CancelablePromise.reject(new Error(type + ' type is not yet supported.'));
        return false;
      }

      // Cancel previous/existing request
      this.requestCancel(layerModel.id);

      // every request method returns a promise that we store in the array
      // to control when all layers are fetched.
      this.promises[layerModel.id] = method.call(this, layerModel, this.providers).then(function (layer) {
        var canceled = _this.promises[layerModel.id]._canceled;

        if (!canceled) {
          layerModel.set('mapLayer', layer);

          _this.plugin.add(layerModel, _this.layers);
          _this.plugin.setZIndex(layerModel, layerModel.zIndex);
          _this.plugin.setOpacity(layerModel, layerModel.opacity);
          _this.plugin.setVisibility(layerModel, layerModel.visibility);

          _this.plugin.setRender(layerModel);

          onAfterAdd(layerModel);
        }
      });

      return this;
    }
  }, {
    key: 'requestCancel',
    value: function requestCancel(layerModelId) {
      // Cancel previous/existing request
      if (this.promises[layerModelId] && this.promises[layerModelId].cancel) {
        this.promises[layerModelId].cancel();
      }
    }
  }, {
    key: 'unmount',
    value: function unmount() {
      this.map = null;
      this.plugin.unmount();
    }
  }]);
  return LayerManager;
}();

/**
 * Params should have this format => { key:'xxx', key2:'xxx' }
 * Keys to search should be in this format {{key}}
 * @param {String} originalStr
 * @param {Object} params
 */
var substitution = function substitution(originalStr) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var str = originalStr;
  Object.keys(params).forEach(function (key) {
    if (Array.isArray(params[key]) || isObject(params[key])) {
      str = str.replace(new RegExp('"{{' + key + '}}"', 'g'), JSON.stringify(params[key])).replace(new RegExp('\'{{' + key + '}}\'', 'g'), JSON.stringify(params[key])).replace(new RegExp('`{{' + key + '}}`', 'g'), JSON.stringify(params[key])).replace(new RegExp('"{' + key + '}"', 'g'), JSON.stringify(params[key])).replace(new RegExp('\'{' + key + '}\'', 'g'), JSON.stringify(params[key])).replace(new RegExp('`{' + key + '}`', 'g'), JSON.stringify(params[key]));
    }

    if (typeof params[key] === 'number' || typeof params[key] === 'boolean') {
      str = str.replace(new RegExp('"{{' + key + '}}"', 'g'), params[key]).replace(new RegExp('\'{{' + key + '}}\'', 'g'), params[key]).replace(new RegExp('`{{' + key + '}}`', 'g'), params[key]).replace(new RegExp('"{' + key + '}"', 'g'), params[key]).replace(new RegExp('\'{' + key + '}\'', 'g'), params[key]).replace(new RegExp('`{' + key + '}`', 'g'), params[key]);
    }

    str = str.replace(new RegExp('{{' + key + '}}', 'g'), params[key]).replace(new RegExp('{' + key + '}', 'g'), params[key]);
  });
  return str;
};

/**
 * Params should have this format => { where1: { { key:'xxx', key2:'xxx' } }},
 * Keys to search should be in this format {{key}}
 * @param {String} originalStr
 * @param {Object} params
 */
var concatenation = function concatenation(originalStr) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var str = originalStr;
  var sql = void 0;

  Object.keys(params).forEach(function (key) {
    sql = '' + compact(Object.keys(params[key]).map(function (k) {
      var value = params[key][k];

      if (Array.isArray(value) && !!value.length) {
        var mappedValue = value.map(function (v) {
          return typeof v !== 'number' ? '\'' + v + '\'' : v;
        });
        return k + ' IN (' + mappedValue.join(', ') + ')';
      }

      if (!Array.isArray(value) && value) {
        return typeof value !== 'number' ? k + ' = \'' + value + '\'' : k + ' = ' + value;
      }

      return null;
    })).join(' AND ');

    if (sql && key.startsWith('where')) sql = 'WHERE ' + sql;else if (sql && key.startsWith('and')) sql = 'AND ' + sql;else sql = '';

    str = str.replace(new RegExp('{{' + key + '}}', 'g'), sql);
    str = str.replace(new RegExp('{' + key + '}', 'g'), sql);
  });

  return str;
};

/**
 * Replace function
 * @param {String} string
 * @param {Object} params
 * @param {Object} sqlParams
 */
var replace = function replace(originalStr) {
  var params = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var sqlParams = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var str = originalStr;

  if (typeof str === 'string') {
    str = substitution(str, params);
    str = concatenation(str, sqlParams);
  }

  return str;
};

/**
 * Parsed vector style layers that follow the mapbox style layer spec
 * and returns mountable layers with ids and opacity applied
 * @param {Array} vectorLayers
 * @param {Object} layerModel
 */
var getVectorStyleLayers = function getVectorStyleLayers(vectorLayers, layerModel) {
  var id = layerModel.id,
      params = layerModel.params,
      sqlParams = layerModel.sqlParams;

  if (vectorLayers && vectorLayers.length) {
    var vectorLayersParsed = JSON.parse(replace(JSON.stringify(vectorLayers), params, sqlParams));
    return vectorLayersParsed && vectorLayersParsed.map(function (vectorLayer, i) {
      var PAINT_STYLE_NAMES = {
        symbol: ['icon', 'text'],
        circle: ['circle', 'circle-stroke']
      };

      // Select the paint property from the original layer
      var _vectorLayer$paint = vectorLayer.paint,
          paint = _vectorLayer$paint === undefined ? {} : _vectorLayer$paint;

      // Select the style to change depending on the type of layer

      var opacityPaintNames = PAINT_STYLE_NAMES[vectorLayer.type] || [vectorLayer.type];

      var opacityPaintStyles = opacityPaintNames.reduce(function (obj, name) {
        var currentProperty = paint[name + '-opacity'];
        var paintOpacity = 0.99 * layerModel.opacity;

        if (currentProperty !== undefined && currentProperty !== null) {
          if (typeof currentProperty === 'number') {
            paintOpacity = currentProperty * layerModel.opacity * 0.99;
          }

          if (Array.isArray(currentProperty)) {
            paintOpacity = currentProperty.map(function (j) {
              if (typeof j === 'number') {
                return j * layerModel.opacity * 0.99;
              }
              return j;
            });
          }
        }

        return _extends({}, obj, defineProperty({}, name + '-opacity', paintOpacity));
      }, {});

      // if paint properties are null are passed it breaks interaction
      // on mapbox. We need to remove these
      var filteredPaintProperties = vectorLayer.paint && Object.entries(vectorLayer.paint).reduce(function (obj, _ref) {
        var _ref2 = slicedToArray(_ref, 2),
            key = _ref2[0],
            value = _ref2[1];

        return _extends({}, obj, !!value && defineProperty({}, key, value));
      }, {});

      return _extends({
        // id: This will avoid having issues with any layer, but you should specify an id when you create it.
        // If you don't set an id in the definition, and you set a fill-opacity, it won't work
        id: id + '-' + vectorLayer.type + '-' + i
      }, vectorLayer, {
        source: id
      }, vectorLayer.paint && {
        paint: _extends({}, filteredPaintProperties, opacityPaintStyles)
      });
    });
  }

  return false;
};

/* eslint-disable */

function getDeckInstance(_ref) {
  var map = _ref.map,
      gl = _ref.gl,
      deck = _ref.deck;

  // Only create one deck instance per context
  if (map.__deck) {
    return map.__deck;
  }

  var customRender = deck && deck.props._customRender;

  var deckProps = {
    useDevicePixels: true,
    _customRender: function _customRender() {
      map.triggerRepaint();
      if (customRender) {
        // customRender may be subscribed by DeckGL React component to update child props
        // make sure it is still called
        customRender();
      }
    },
    // TODO: import these defaults from a single source of truth
    parameters: {
      depthMask: true,
      depthTest: true,
      blendFunc: [gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA],
      blendEquation: gl.FUNC_ADD
    },
    userData: {
      isExternal: false,
      mapboxLayers: new Set()
    }
  };

  if (deck) {
    deck.setProps(deckProps);
    deck.props.userData.isExternal = true;
  } else {
    // Using external gl context - do not set css size
    Object.assign(deckProps, {
      gl: gl,
      width: false,
      height: false,
      viewState: getViewState(map)
    });
    deck = new Deck(deckProps);

    // If deck is externally provided (React use case), we use deck's viewState to
    // drive the map.
    // Otherwise (pure JS use case), we use the map's viewState to drive deck.
    map.on('move', function () {
      return onMapMove(deck, map);
    });
    map.on('remove', function () {
      deck.finalize();
      map.__deck = null;
    });
  }
  deck.props.userData.mapboxVersion = getMapboxVersion(map);
  map.__deck = deck;
  map.on('render', function () {
    return afterRender(deck, map);
  });

  return deck;
}

function addLayer(deck, layer) {
  deck.props.userData.mapboxLayers.add(layer);
  updateLayers(deck);
}

function removeLayer(deck, layer) {
  deck.props.userData.mapboxLayers.delete(layer);
  updateLayers(deck);
}

function updateLayer(deck, layer) {
  updateLayers(deck);
}

function drawLayer(deck, map, layer) {
  var currentViewport = deck.props.userData.currentViewport;

  if (!currentViewport) {
    // This is the first layer drawn in this render cycle.
    // Generate viewport from the current map state.
    currentViewport = getViewport(deck, map, true);
    deck.props.userData.currentViewport = currentViewport;
  }

  deck._drawLayers('mapbox-repaint', {
    viewports: [currentViewport],
    // TODO - accept layerFilter in drawLayers' renderOptions
    layers: getLayers(deck, function (deckLayer) {
      return shouldDrawLayer(layer.id, deckLayer);
    }),
    clearCanvas: false
  });

  // if (ANIMATION) cancelAnimationFrame(ANIMATION);

  // ANIMATION = requestAnimationFrame(() => {
  //   deck._drawLayers('mapbox-repaint', {
  //     clearCanvas: false
  //   });
  // });
}

function getViewState(map) {
  var _map$getCenter = map.getCenter(),
      lng = _map$getCenter.lng,
      lat = _map$getCenter.lat;

  return {
    longitude: lng,
    latitude: lat,
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch()
  };
}

function getMapboxVersion(map) {
  // parse mapbox version string
  var major = 0;
  var minor = 0;
  if (map.version) {
    var _map$version$split$sl = map.version.split('.').slice(0, 2).map(Number);

    var _map$version$split$sl2 = slicedToArray(_map$version$split$sl, 2);

    major = _map$version$split$sl2[0];
    minor = _map$version$split$sl2[1];
  }
  return { major: major, minor: minor };
}

function getViewport(deck, map) {
  var useMapboxProjection = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var mapboxVersion = deck.props.userData.mapboxVersion;


  return new WebMercatorViewport(Object.assign({
    x: 0,
    y: 0,
    width: deck.width,
    height: deck.height
  }, getViewState(map), useMapboxProjection ? {
    // match mapbox's projection matrix
    // A change of near plane was made in 1.3.0
    // https://github.com/mapbox/mapbox-gl-js/pull/8502
    nearZMultiplier: mapboxVersion.major === 1 && mapboxVersion.minor >= 3 || mapboxVersion.major >= 2 ? 0.02 : 1 / (deck.height || 1)
  } : {
    // use deck.gl's own default
    nearZMultiplier: 0.1
  }));
}

function afterRender(deck, map) {
  var _deck$props$userData = deck.props.userData,
      mapboxLayers = _deck$props$userData.mapboxLayers,
      isExternal = _deck$props$userData.isExternal;


  if (isExternal) {
    // Draw non-Mapbox layers
    var mapboxLayerIds = Array.from(mapboxLayers, function (layer) {
      return layer.id;
    });
    var layers = getLayers(deck, function (deckLayer) {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = mapboxLayerIds[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var id = _step.value;

          if (shouldDrawLayer(id, deckLayer)) {
            return false;
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      return true;
    });
    if (layers.length > 0) {
      deck._drawLayers('mapbox-repaint', {
        viewports: [getViewport(deck, map, false)],
        layers: layers,
        clearCanvas: false
      });
    }
  }

  // End of render cycle, clear generated viewport
  deck.props.userData.currentViewport = null;
}

function onMapMove(deck, map) {
  deck.setProps({
    viewState: getViewState(map)
  });
  // Camera changed, will trigger a map repaint right after this
  // Clear any change flag triggered by setting viewState so that deck does not request
  // a second repaint
  // deck.needsRedraw({clearRedrawFlags: true});
}

function getLayers(deck, layerFilter) {
  var layers = deck.layerManager.getLayers();
  return layers.filter(layerFilter);
}

function shouldDrawLayer(id, layer) {
  var layerInstance = layer;
  while (layerInstance) {
    if (layerInstance.id === id) {
      return true;
    }
    layerInstance = layerInstance.parent;
  }
  return false;
}

function updateLayers(deck) {
  if (deck.props.userData.isExternal) {
    return;
  }

  var layers = [];
  deck.props.userData.mapboxLayers.forEach(function (deckLayer) {
    var LayerType = deckLayer.props.type;
    var layer = new LayerType(deckLayer.props);
    layers.push(layer);
  });
  deck.setProps({ layers: layers });
}

var MapboxLayer = function () {
  /* eslint-disable no-this-before-super */
  function MapboxLayer(props) {
    classCallCheck(this, MapboxLayer);

    if (!props.id) {
      throw new Error('Layer must have an unique id');
    }

    this.id = props.id;
    this.type = 'custom';
    this.renderingMode = props.renderingMode || '3d';
    this.map = null;
    this.deck = null;
    this.props = props;
  }

  /* Mapbox custom layer methods */

  createClass(MapboxLayer, [{
    key: 'onAdd',
    value: function onAdd(map, gl) {
      this.map = map;
      this.deck = getDeckInstance({ map: map, gl: gl, deck: this.props.deck });
      addLayer(this.deck, this);
    }
  }, {
    key: 'onRemove',
    value: function onRemove() {
      removeLayer(this.deck, this);
    }
  }, {
    key: 'setProps',
    value: function setProps(props) {
      // id cannot be changed
      Object.assign(this.props, props, { id: this.id });
      // safe guard in case setProps is called before onAdd
      if (this.deck) {
        updateLayer(this.deck, this);
      }
    }
  }, {
    key: 'render',
    value: function render() {
      drawLayer(this.deck, this.map, this);
    }
  }]);
  return MapboxLayer;
}();

// https://wiki.openstreetmap.org/wiki/Slippy_map_tilenames#Lon..2Flat._to_tile_numbers_2
function tile2latLng(x, y, z) {
  var lng = x / Math.pow(2, z) * 360 - 180;
  var n = Math.PI - 2 * Math.PI * y / Math.pow(2, z);
  var lat = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return [lng, lat];
}

function tile2boundingBox(x, y, z) {
  var _tile2latLng = tile2latLng(x, y, z),
      _tile2latLng2 = slicedToArray(_tile2latLng, 2),
      west = _tile2latLng2[0],
      north = _tile2latLng2[1];

  var _tile2latLng3 = tile2latLng(x + 1, y + 1, z),
      _tile2latLng4 = slicedToArray(_tile2latLng3, 2),
      east = _tile2latLng4[0],
      south = _tile2latLng4[1];

  return [west, south, east, north];
}

var Tile = function () {
  function Tile(_ref) {
    var getTileData = _ref.getTileData,
        x = _ref.x,
        y = _ref.y,
        z = _ref.z,
        onTileLoad = _ref.onTileLoad,
        onTileError = _ref.onTileError;
    classCallCheck(this, Tile);

    this.x = x;
    this.y = y;
    this.z = z;
    this.bbox = tile2boundingBox(this.x, this.y, this.z);
    this.isVisible = true;
    this.getTileData = getTileData;
    this._data = null;
    this._isLoaded = false;
    this._loader = this._loadData();
    this.onTileLoad = onTileLoad;
    this.onTileError = onTileError;
  }

  createClass(Tile, [{
    key: "_loadData",
    value: function _loadData() {
      var _this = this;

      var x = this.x,
          y = this.y,
          z = this.z,
          bbox = this.bbox;

      if (!this.getTileData) {
        return null;
      }

      return Promise.resolve(this.getTileData({ x: x, y: y, z: z, bbox: bbox })).then(function (buffers) {
        _this._data = buffers;
        _this._isLoaded = true;
        _this.onTileLoad(_this);
        return buffers;
      }).catch(function (err) {
        _this._isLoaded = true;
        _this.onTileError(err);
      });
    }
  }, {
    key: "isOverlapped",
    value: function isOverlapped(tile) {
      var x = this.x,
          y = this.y,
          z = this.z;

      var m = Math.pow(2, tile.z - z);
      return Math.floor(tile.x / m) === x && Math.floor(tile.y / m) === y;
    }
  }, {
    key: "data",
    get: function get$$1() {
      return this._data || this._loader;
    }
  }, {
    key: "isLoaded",
    get: function get$$1() {
      return this._isLoaded;
    }
  }]);
  return Tile;
}();

var TILE_SIZE = 256;

function getBoundingBox(viewport) {
  var corners = [viewport.unproject([0, 0]), viewport.unproject([viewport.width, 0]), viewport.unproject([0, viewport.height]), viewport.unproject([viewport.width, viewport.height])];

  return [corners.reduce(function (minLng, p) {
    return minLng < p[0] ? minLng : p[0];
  }, 180), corners.reduce(function (minLat, p) {
    return minLat < p[1] ? minLat : p[1];
  }, 90), corners.reduce(function (maxLng, p) {
    return maxLng > p[0] ? maxLng : p[0];
  }, -180), corners.reduce(function (maxLat, p) {
    return maxLat > p[1] ? maxLat : p[1];
  }, -90)];
}

function pixelsToTileIndex(a) {
  return a / TILE_SIZE;
}

/**
 * Calculates and returns a new tile index {x, y, z}, with z being the given adjustedZ.
 */
function getAdjustedTileIndex(_ref, adjustedZ) {
  var x = _ref.x,
      y = _ref.y,
      z = _ref.z;

  var m = Math.pow(2, z - adjustedZ);
  return {
    x: Math.floor(x / m),
    y: Math.floor(y / m),
    z: adjustedZ
  };
}

/**
 * Returns all tile indices in the current viewport. If the current zoom level is smaller
 * than minZoom, return an empty array. If the current zoom level is greater than maxZoom,
 * return tiles that are on maxZoom.
 */
function getTileIndices(viewport, maxZoom, minZoom) {
  var z = Math.floor(viewport.zoom) + 1;
  if (minZoom && z < minZoom) {
    return [];
  }

  // eslint-disable-next-line no-param-reassign
  viewport = new viewport.constructor(_extends({}, viewport, {
    zoom: z - 1
  }));

  var bbox = getBoundingBox(viewport);

  var _lngLatToWorld$map = lngLatToWorld([bbox[0], bbox[3]], viewport.scale).map(pixelsToTileIndex),
      _lngLatToWorld$map2 = slicedToArray(_lngLatToWorld$map, 2),
      minX = _lngLatToWorld$map2[0],
      minY = _lngLatToWorld$map2[1];

  var _lngLatToWorld$map3 = lngLatToWorld([bbox[2], bbox[1]], viewport.scale).map(pixelsToTileIndex),
      _lngLatToWorld$map4 = slicedToArray(_lngLatToWorld$map3, 2),
      maxX = _lngLatToWorld$map4[0],
      maxY = _lngLatToWorld$map4[1];

  minX = Math.floor(minX);
  maxX = Math.ceil(maxX);
  minY = Math.floor(minY);
  maxY = Math.ceil(maxY);

  var indices = [];

  for (var x = minX; x < maxX; x++) {
    for (var y = minY; y < maxY; y++) {
      if (maxZoom && z > maxZoom) {
        indices.push(getAdjustedTileIndex({ x: x, y: y, z: z }, maxZoom));
      } else {
        indices.push({ x: x, y: y, z: z });
      }
    }
  }

  return indices;
}

/* eslint-disable class-methods-use-this */

/**
 * Manages loading and purging of tiles data. This class caches recently visited tiles
 * and only create new tiles if they are present.
 */

var TileCache = function () {
  /**
   * Takes in a function that returns tile data, a cache size, and a max and a min zoom level.
   * Cache size defaults to 5 * number of tiles in the current viewport
   */
  function TileCache(_ref) {
    var getTileData = _ref.getTileData,
        maxSize = _ref.maxSize,
        maxZoom = _ref.maxZoom,
        minZoom = _ref.minZoom,
        onTileLoad = _ref.onTileLoad,
        onTileError = _ref.onTileError;
    classCallCheck(this, TileCache);

    // TODO: Instead of hardcode size, we should calculate how much memory left
    this._getTileData = getTileData;
    this._maxSize = maxSize;
    this.onTileError = onTileError;
    this.onTileLoad = onTileLoad;

    // Maps tile id in string {z}-{x}-{y} to a Tile object
    this._cache = new Map();
    this._tiles = [];

    if (Number.isFinite(maxZoom)) {
      this._maxZoom = Math.floor(maxZoom);
    }
    if (Number.isFinite(minZoom)) {
      this._minZoom = Math.ceil(minZoom);
    }
  }

  createClass(TileCache, [{
    key: 'finalize',


    /**
     * Clear the current cache
     */
    value: function finalize() {
      this._cache.clear();
    }

    /**
     * Update the cache with the given viewport and triggers callback onUpdate.
     * @param {*} viewport
     * @param {*} onUpdate
     */

  }, {
    key: 'update',
    value: function update(viewport) {
      var _cache = this._cache,
          _getTileData = this._getTileData,
          _maxSize = this._maxSize,
          _maxZoom = this._maxZoom,
          _minZoom = this._minZoom;

      this._markOldTiles();
      var tileIndices = getTileIndices(viewport, _maxZoom, _minZoom);
      if (!tileIndices || tileIndices.length === 0) {
        return;
      }
      _cache.forEach(function (cachedTile) {
        if (tileIndices.some(function (tile) {
          return cachedTile.isOverlapped(tile);
        })) {
          cachedTile.isVisible = true; // eslint-disable-line
        }
      });

      var changed = false;

      for (var i = 0; i < tileIndices.length; i++) {
        var tileIndex = tileIndices[i];

        var x = tileIndex.x,
            y = tileIndex.y,
            z = tileIndex.z;

        var tile = this._getTile(x, y, z);
        if (!tile) {
          tile = new Tile({
            getTileData: _getTileData,
            x: x,
            y: y,
            z: z,
            onTileLoad: this.onTileLoad,
            onTileError: this.onTileError
          });
          tile.isVisible = true;
          changed = true;
        }
        var tileId = this._getTileId(x, y, z);
        _cache.set(tileId, tile);
      }

      if (changed) {
        // cache size is either the user defined maxSize or 5 * number of current tiles in the viewport.
        var commonZoomRange = 5;
        this._resizeCache(_maxSize || commonZoomRange * tileIndices.length);
        this._tiles = Array.from(this._cache.values())
        // sort by zoom level so parents tiles don't show up when children tiles are rendered
        .sort(function (t1, t2) {
          return t1.z - t2.z;
        });
      }
    }

    /**
     * Clear tiles that are not visible when the cache is full
     */

  }, {
    key: '_resizeCache',
    value: function _resizeCache(maxSize) {
      var _cache = this._cache;

      if (_cache.size > maxSize) {
        var iterator = _cache[Symbol.iterator]();

        // eslint-disable-next-line no-restricted-syntax
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
          for (var _iterator = iterator[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var cachedTile = _step.value;

            if (_cache.size <= maxSize) {
              break;
            }
            var tileId = cachedTile[0];
            var tile = cachedTile[1];
            if (!tile.isVisible) {
              _cache.delete(tileId);
            }
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator.return) {
              _iterator.return();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }
      }
    }
  }, {
    key: '_markOldTiles',
    value: function _markOldTiles() {
      this._cache.forEach(function (cachedTile) {
        // eslint-disable-next-line no-param-reassign
        cachedTile.isVisible = false;
      });
    }
  }, {
    key: '_getTile',
    value: function _getTile(x, y, z) {
      var tileId = this._getTileId(x, y, z);
      return this._cache.get(tileId);
    }
  }, {
    key: '_getTileId',
    value: function _getTileId(x, y, z) {
      return z + '-' + x + '-' + y;
    }
  }, {
    key: 'tiles',
    get: function get$$1() {
      return this._tiles;
    }
  }]);
  return TileCache;
}();

/* eslint-disable no-param-reassign,no-restricted-properties,class-methods-use-this,no-underscore-dangle */

var defaultProps = {
  renderSubLayers: { type: 'function', value: function value() {
      return null;
    } },
  getTileData: { type: 'function', value: function value() {
      return Promise.resolve(null);
    } },
  // TODO - change to onViewportLoad to align with Tile3DLayer
  onViewportLoaded: { type: 'function', optional: true, value: null },
  // eslint-disable-next-line
  onTileError: { type: 'function', value: function value(err) {
      return console.error(err);
    } },
  maxZoom: null,
  minZoom: 0,
  maxCacheSize: null
};

var TileLayer = function (_CompositeLayer) {
  inherits(TileLayer, _CompositeLayer);

  function TileLayer() {
    classCallCheck(this, TileLayer);
    return possibleConstructorReturn(this, (TileLayer.__proto__ || Object.getPrototypeOf(TileLayer)).apply(this, arguments));
  }

  createClass(TileLayer, [{
    key: 'initializeState',
    value: function initializeState() {
      this.state = {
        tiles: [],
        isLoaded: false
      };
    }
  }, {
    key: 'shouldUpdateState',
    value: function shouldUpdateState(_ref) {
      var changeFlags = _ref.changeFlags;

      return changeFlags.somethingChanged;
    }
  }, {
    key: 'updateState',
    value: function updateState(_ref2) {
      var props = _ref2.props,
          context = _ref2.context,
          changeFlags = _ref2.changeFlags;
      var tileCache = this.state.tileCache;

      if (!tileCache || changeFlags.updateTriggersChanged && (changeFlags.updateTriggersChanged.all || changeFlags.updateTriggersChanged.getTileData)) {
        var getTileData = props.getTileData,
            maxZoom = props.maxZoom,
            minZoom = props.minZoom,
            maxCacheSize = props.maxCacheSize;

        if (tileCache) {
          tileCache.finalize();
        }
        tileCache = new TileCache({
          getTileData: getTileData,
          maxSize: maxCacheSize,
          maxZoom: maxZoom,
          minZoom: minZoom,
          onTileLoad: this._onTileLoad.bind(this),
          onTileError: this._onTileError.bind(this)
        });

        this.setState({ tileCache: tileCache });
      } else if (changeFlags.updateTriggersChanged) {
        // if any updateTriggersChanged (other than getTileData), delete the layer
        this.state.tileCache.tiles.forEach(function (tile) {
          tile.layer = null;
        });
      }

      var viewport = context.viewport;

      if (changeFlags.viewportChanged && viewport.id !== 'DEFAULT-INITIAL-VIEWPORT') {
        var z = this.getLayerZoomLevel();
        tileCache.update(viewport);
        // The tiles that should be displayed at this zoom level
        var currTiles = tileCache.tiles.filter(function (tile) {
          return tile.z === z;
        });
        this.setState({ isLoaded: false, tiles: currTiles });
        this._onTileLoad();
      }
    }
  }, {
    key: '_onTileLoad',
    value: function _onTileLoad() {
      var onViewportLoaded = this.props.onViewportLoaded;

      var currTiles = this.state.tiles;
      var allCurrTilesLoaded = currTiles.every(function (tile) {
        return tile.isLoaded;
      });

      if (this.state.isLoaded !== allCurrTilesLoaded) {
        this.setState({ isLoaded: allCurrTilesLoaded });
        if (allCurrTilesLoaded && onViewportLoaded) {
          onViewportLoaded(currTiles.filter(function (tile) {
            return tile._data;
          }).map(function (tile) {
            return tile._data;
          }));
        }
      }
    }
  }, {
    key: '_onTileError',
    value: function _onTileError(error) {
      this.props.onTileError(error);
      // errorred tiles should not block rendering, are considered "loaded" with empty data
      this._onTileLoad();
    }
  }, {
    key: 'getPickingInfo',
    value: function getPickingInfo(_ref3) {
      var info = _ref3.info,
          sourceLayer = _ref3.sourceLayer;

      info.sourceLayer = sourceLayer;
      info.tile = sourceLayer.props.tile;
      return info;
    }
  }, {
    key: 'getLayerZoomLevel',
    value: function getLayerZoomLevel() {
      var z = Math.floor(this.context.viewport.zoom) + 1;
      var _props = this.props,
          maxZoom = _props.maxZoom,
          minZoom = _props.minZoom;

      if (Number.isFinite(maxZoom) && z > maxZoom) {
        return Math.floor(maxZoom);
      }

      if (Number.isFinite(minZoom) && z < minZoom) {
        return Math.ceil(minZoom);
      }
      return z;
    }
  }, {
    key: 'renderLayers',
    value: function renderLayers() {
      var _this2 = this;

      var _props2 = this.props,
          visible = _props2.visible,
          opacity = _props2.opacity,
          decodeParams = _props2.decodeParams,
          renderSubLayers = _props2.renderSubLayers;

      var z = this.getLayerZoomLevel();

      return this.state.tiles.map(function (tile) {
        // For a tile to be visible:
        // - parent layer must be visible
        // - tile must be visible in the current viewport
        // - if all tiles are loaded, only display the tiles from the current z level
        var isVisible = visible && tile.isVisible && (!_this2.state.isLoaded || tile.z === z);

        // cache the rendered layer in the tile
        if (!tile.layer) {
          // I have an error when I try this condition. I think is related to return the same tile.layer
          // https://github.com/uber/deck.gl/blob/c616dbfbcbdc9d8533b88ba8a142c5527764bbd8/modules/core/src/lib/layer-manager.js#L67-L70
          tile.layer = renderSubLayers(_extends({}, _this2.props, {
            id: _this2.id + '-' + tile.x + '-' + tile.y + '-' + tile.z,
            data: tile.data,
            visible: isVisible,
            opacity: opacity,
            tile: tile,
            zoom: z
          }));
        } else if (tile.layer.props.visible !== isVisible) {
          tile.layer = tile.layer.clone({
            visible: isVisible
          });
        } else if (tile.layer.props.opacity !== opacity) {
          tile.layer = tile.layer.clone({
            opacity: opacity
          });
        } else if (tile.layer.props.decodeParams) {
          tile.layer = tile.layer.clone({
            decodeParams: decodeParams
          });
        }
        return tile.layer;
      });
    }
  }]);
  return TileLayer;
}(CompositeLayer);


TileLayer.layerName = 'TileLayer';
TileLayer.defaultProps = defaultProps;

var decodeVX = "\n#define SHADER_NAME bitmap-layer-vertex-shader\n\nattribute vec2 texCoords;\nattribute vec3 positions;\nattribute vec2 positions64xyLow;\n\nvarying vec2 vTexCoord;\n\nvoid main(void) {\n  gl_Position = project_position_to_clipspace(positions, positions64xyLow, vec3(0.0));\n\n  vTexCoord = texCoords;\n\n  picking_setPickingColor(vec3(0., 0., 1.));\n}\n";

var decodeFR = "\n#define SHADER_NAME bitmap-layer-fragment-shader\n\n#ifdef GL_ES\nprecision highp float;\n#endif\n\nuniform sampler2D bitmapTexture;\n\nvarying vec2 vTexCoord;\n\n{decodeParams}\n\nuniform float opacity;\nuniform float zoom;\n\n\nvec4 decodeFunction(vec3 color, float alpha) {\n  {decodeFunction}\n  return vec4(color, alpha * opacity);\n}\n\nvoid main(void) {\n  vec4 bitmapColor = texture2D(bitmapTexture, vTexCoord);\n\n  // clear mask\n  if (bitmapColor == vec4(0., 0., 0., 1.)) {\n    discard;\n  }\n\n  // custom decode\n  gl_FragColor = decodeFunction(bitmapColor.rgb, bitmapColor.a);\n\n  // use highlight color if this fragment belongs to the selected object.\n  gl_FragColor = picking_filterHighlightColor(gl_FragColor);\n\n  // use picking color if rendering to picking FBO.\n  gl_FragColor = picking_filterPickingColor(gl_FragColor);\n}\n";

var _DEFAULT_TEXTURE_PARA;

var fp64LowPart = fp64.fp64LowPart;


var DEFAULT_TEXTURE_PARAMETERS = (_DEFAULT_TEXTURE_PARA = {}, defineProperty(_DEFAULT_TEXTURE_PARA, GL.TEXTURE_MIN_FILTER, GL.NEAREST), defineProperty(_DEFAULT_TEXTURE_PARA, GL.TEXTURE_MAG_FILTER, GL.NEAREST), defineProperty(_DEFAULT_TEXTURE_PARA, GL.TEXTURE_WRAP_S, GL.CLAMP_TO_EDGE), defineProperty(_DEFAULT_TEXTURE_PARA, GL.TEXTURE_WRAP_T, GL.CLAMP_TO_EDGE), _DEFAULT_TEXTURE_PARA);

var defaultProps$1 = {
  image: null,
  bounds: { type: 'array', value: [1, 0, 0, 1], compare: true },
  fp64: false,

  desaturate: { type: 'number', min: 0, max: 1, value: 0 },
  // More context: because of the blending mode we're using for ground imagery,
  // alpha is not effective when blending the bitmap layers with the base map.
  // Instead we need to manually dim/blend rgb values with a background color.
  transparentColor: { type: 'color', value: [0, 0, 0, 0] },
  tintColor: { type: 'color', value: [255, 255, 255] }
};

/*
 * @class
 * @param {object} props
 * @param {number} props.transparentColor - color to interpret transparency to
 * @param {number} props.tintColor - color bias
 */

var DecodeLayer = function (_Layer) {
  inherits(DecodeLayer, _Layer);

  function DecodeLayer() {
    classCallCheck(this, DecodeLayer);
    return possibleConstructorReturn(this, (DecodeLayer.__proto__ || Object.getPrototypeOf(DecodeLayer)).apply(this, arguments));
  }

  createClass(DecodeLayer, [{
    key: 'getShaders',
    value: function getShaders() {
      var projectModule = 'project32';
      var fs = decodeFR.replace('{decodeParams}', Object.keys(this.props.decodeParams).map(function (p) {
        return 'uniform float ' + p + ';';
      }).join(' ')).replace('{decodeFunction}', this.props.decodeFunction || '');

      return { vs: decodeVX, fs: fs, modules: [projectModule, 'picking'] };
    }
  }, {
    key: 'initializeState',
    value: function initializeState() {
      var attributeManager = this.getAttributeManager();

      attributeManager.add({
        positions: {
          size: 3,
          update: this.calculatePositions,
          value: new Float32Array(12)
        },
        positions64xyLow: {
          size: 3,
          update: this.calculatePositions64xyLow,
          value: new Float32Array(12)
        }
      });

      this.setState({ numInstances: 4 }); // 4 corners
    }
  }, {
    key: 'updateState',
    value: function updateState(_ref) {
      var props = _ref.props,
          oldProps = _ref.oldProps;

      // setup model first
      if (props.fp64 !== oldProps.fp64) {
        var gl = this.context.gl;

        if (this.state.model) {
          this.state.model.delete();
        }
        this.setState({ model: this._getModel(gl) });
        this.getAttributeManager().invalidateAll();
      }

      if (props.image !== oldProps.image) {
        this.loadTexture(props.image);
      }

      var attributeManager = this.getAttributeManager();

      if (props.bounds !== oldProps.bounds) {
        this.setState({
          positions: this._getPositionsFromBounds(props.bounds)
        });
        attributeManager.invalidate('positions');
        attributeManager.invalidate('positions64xyLow');
      }
    }
  }, {
    key: 'finalizeState',
    value: function finalizeState() {
      get(DecodeLayer.prototype.__proto__ || Object.getPrototypeOf(DecodeLayer.prototype), 'finalizeState', this).call(this);

      if (this.state.bitmapTexture) {
        this.state.bitmapTexture.delete();
      }
    }
  }, {
    key: '_getPositionsFromBounds',
    value: function _getPositionsFromBounds(bounds) {
      var positions = new Array(12);
      // bounds as [minX, minY, maxX, maxY]
      if (Number.isFinite(bounds[0])) {
        /*
          (minX0, maxY3) ---- (maxX2, maxY3)
                 |                  |
                 |                  |
                 |                  |
          (minX0, minY1) ---- (maxX2, minY1)
        */
        positions[0] = bounds[0];
        positions[1] = bounds[1];
        positions[2] = 0;

        positions[3] = bounds[0];
        positions[4] = bounds[3];
        positions[5] = 0;

        positions[6] = bounds[2];
        positions[7] = bounds[3];
        positions[8] = 0;

        positions[9] = bounds[2];
        positions[10] = bounds[1];
        positions[11] = 0;
      } else {
        // [[minX, minY], [minX, maxY], [maxX, maxY], [maxX, minY]]
        for (var i = 0; i < bounds.length; i++) {
          positions[i * 3 + 0] = bounds[i][0];
          positions[i * 3 + 1] = bounds[i][1];
          positions[i * 3 + 2] = bounds[i][2] || 0;
        }
      }

      return positions;
    }
  }, {
    key: '_getModel',
    value: function _getModel(gl) {
      if (!gl) {
        return null;
      }

      /*
        0,1 --- 1,1
         |       |
        0,0 --- 1,0
      */
      return new Model(gl, Object.assign({}, this.getShaders(), {
        id: this.props.id,
        shaderCache: this.context.shaderCache,
        geometry: new Geometry({
          drawMode: GL.TRIANGLE_FAN,
          vertexCount: 4,
          attributes: {
            texCoords: new Float32Array([0, 0, 0, 1, 1, 1, 1, 0])
          }
        }),
        isInstanced: false
      }));
    }
  }, {
    key: 'draw',
    value: function draw(_ref2) {
      var uniforms = _ref2.uniforms;
      var _state = this.state,
          bitmapTexture = _state.bitmapTexture,
          model = _state.model;
      var _props = this.props,
          desaturate = _props.desaturate,
          transparentColor = _props.transparentColor,
          tintColor = _props.tintColor,
          zoom = _props.zoom,
          decodeParams = _props.decodeParams,
          opacity = _props.opacity;

      // // TODO fix zFighting
      // Render the image

      if (bitmapTexture && model) {
        model.setUniforms(Object.assign({}, uniforms, _extends({
          bitmapTexture: bitmapTexture,
          desaturate: desaturate,
          transparentColor: transparentColor,
          tintColor: tintColor,
          zoom: zoom
        }, decodeParams, {
          opacity: opacity
        }))).draw();
      }
    }
  }, {
    key: 'loadTexture',
    value: function loadTexture(image) {
      var _this2 = this;

      if (typeof image === 'string') {
        image = loadImage(image);
      }
      if (image instanceof Promise) {
        image.then(function (data) {
          return _this2.loadTexture(data);
        });
        return;
      }

      var gl = this.context.gl;


      if (this.state.bitmapTexture) {
        this.state.bitmapTexture.delete();
      }

      if (image instanceof Texture2D) {
        this.setState({ bitmapTexture: image });
      } else if (
      // browser object
      image instanceof Image || image instanceof HTMLCanvasElement) {
        this.setState({
          bitmapTexture: new Texture2D(gl, {
            data: image,
            parameters: DEFAULT_TEXTURE_PARAMETERS,
            mipmaps: false
          })
        });
      }
    }
  }, {
    key: 'calculatePositions',
    value: function calculatePositions(_ref3) {
      var value = _ref3.value;
      var positions = this.state.positions;

      value.set(positions);
    }
  }, {
    key: 'calculatePositions64xyLow',
    value: function calculatePositions64xyLow(attribute) {
      var isFP64 = this.use64bitPositions();
      attribute.constant = !isFP64;

      if (!isFP64) {
        attribute.value = new Float32Array(4);
        return;
      }

      var value = attribute.value;

      value.set(this.state.positions.map(fp64LowPart));
    }
  }]);
  return DecodeLayer;
}(Layer);


DecodeLayer.layerName = 'DecodeLayer';
DecodeLayer.defaultProps = defaultProps$1;

var _this = undefined;

var _getTileData = function _getTileData(_ref, url) {
  var x = _ref.x,
      y = _ref.y,
      z = _ref.z;

  var mapSource = url.replace('{z}', z).replace('{x}', x).replace('{y}', y);

  return fetch(mapSource).then(function (response) {
    return response.blob();
  }).then(function (response) {
    var _ref2 = response || {},
        type = _ref2.type;

    if (type !== 'application/xml' && type !== 'text/xml' && type !== 'text/html') {
      var src = URL.createObjectURL(response);
      var image = new Image();

      image.src = src;
      return image;
    }

    return false;
  });
};

var RasterLayer = function RasterLayer(layerModel, providers) {
  var _layerModel$source = layerModel.source,
      source = _layerModel$source === undefined ? {} : _layerModel$source,
      _layerModel$render = layerModel.render,
      render = _layerModel$render === undefined ? {} : _layerModel$render,
      decodeParams = layerModel.decodeParams,
      id = layerModel.id,
      opacity = layerModel.opacity,
      decodeFunction = layerModel.decodeFunction;


  var DEFAULT_RASTER_OPTIONS = {
    id: id + '-raster',
    type: 'raster',
    source: id
  };

  var provider = source.provider;
  var _render$layers = render.layers,
      layers = _render$layers === undefined ? [DEFAULT_RASTER_OPTIONS] : _render$layers;


  var layer = {};

  // if decoded layer use custom deck layer
  if (decodeFunction) {
    layer = {
      id: id,
      type: 'custom',
      layers: [{
        id: id + '-raster-decode-bg',
        type: 'background',
        paint: {
          'background-color': 'transparent'
        }
      }].concat(toConsumableArray(source.tiles.map(function (t) {
        return new MapboxLayer({
          id: id + '-raster-decode',
          type: TileLayer,
          getTileData: function getTileData(e) {
            return _getTileData(e, t);
          },
          renderSubLayers: function renderSubLayers(_ref3) {
            var subLayerId = _ref3.id,
                data = _ref3.data,
                tile = _ref3.tile,
                visible = _ref3.visible,
                zoom = _ref3.zoom,
                decodeParamsSub = _ref3.decodeParams,
                decodeFunctionSub = _ref3.decodeFunction;

            if (data && data.src) {
              return new DecodeLayer({
                id: subLayerId,
                image: data.src,
                bounds: tile.bbox,
                visible: visible,
                zoom: zoom,
                decodeParams: decodeParamsSub,
                decodeFunction: decodeFunctionSub,
                opacity: opacity
              });
            }
            return null;
          },
          minZoom: source.minzoom,
          maxZoom: source.maxzoom,
          opacity: layerModel.opacity,
          decodeParams: decodeParams,
          decodeFunction: decodeFunction
        });
      })))
    };
  } else {
    layer = {
      id: id,
      type: 'raster',
      source: _extends({
        type: 'raster',
        tileSize: 256
      }, source),
      layers: getVectorStyleLayers(layers.map(function (l) {
        return _extends({}, DEFAULT_RASTER_OPTIONS, l);
      }), layerModel)
    };
  }

  if (provider) {
    var method = providers[provider.type];

    return new CancelablePromise(function (resolve, reject) {
      if (!method) {
        reject(new Error(provider.type + ' provider is not supported. Try to add it to the providers method when you initialize layer-manager'));
      }

      method.call(_this, layerModel, layer, resolve, reject);
    });
  }

  return new CancelablePromise(function (resolve, reject) {
    if (layer) {
      resolve(layer);
    } else {
      reject(new Error('error in layer config'));
    }
  });
};

var _this$1 = undefined;

var VectorLayer = function VectorLayer(layerModel, providers) {
  var _layerModel$source = layerModel.source,
      source = _layerModel$source === undefined ? {} : _layerModel$source,
      _layerModel$render = layerModel.render,
      render = _layerModel$render === undefined ? {} : _layerModel$render,
      id = layerModel.id;
  var provider = source.provider;
  var layers = render.layers;


  var layer = _extends({
    id: id,
    source: _extends({
      type: 'vector'
    }, source)
  }, render, {
    layers: getVectorStyleLayers(layers, layerModel)
  });

  if (provider) {
    var method = providers[provider.type];

    return new CancelablePromise(function (resolve, reject) {
      if (!method) {
        reject(new Error(provider.type + ' provider is not supported. Try to add it to the providers method when you initialize layer-manager'));
      }

      method.call(_this$1, layerModel, layer, resolve, reject);
    });
  }

  return new CancelablePromise(function (resolve, reject) {
    if (layer) {
      resolve(layer);
    } else {
      reject(new Error('error in layer config'));
    }
  });
};

var _this$2 = undefined;

var GeoJsonLayer = function GeoJsonLayer(layerModel, providers) {
  var _layerModel$source = layerModel.source,
      source = _layerModel$source === undefined ? {} : _layerModel$source,
      _layerModel$render = layerModel.render,
      render = _layerModel$render === undefined ? {} : _layerModel$render,
      id = layerModel.id;
  var provider = source.provider;
  var layers = render.layers;


  var layer = _extends({
    id: id,
    source: _extends({
      type: 'geojson'
    }, source)
  }, render, {
    layers: getVectorStyleLayers(layers, layerModel)
  });

  if (provider) {
    var method = providers[provider.type];

    return new CancelablePromise(function (resolve, reject) {
      if (!method) {
        reject(new Error(provider.type + ' provider is not supported. Try to add it to the providers method when you initialize layer-manager'));
      }

      method.call(_this$2, layerModel, layer, resolve, reject);
    });
  }

  return new CancelablePromise(function (resolve, reject) {
    if (layer) {
      resolve(layer);
    } else {
      reject(new Error('"type" specified in layer spec doesn`t exist'));
    }
  });
};

var VideoLayer = function VideoLayer(layerModel) {
  var _layerModel$source = layerModel.source,
      source = _layerModel$source === undefined ? {} : _layerModel$source,
      render = layerModel.render,
      id = layerModel.id;


  var layer = _extends({
    id: id,
    type: 'raster',
    source: _extends({}, source, {
      type: 'video-tiled',
      // tiles: [ "https://storage.googleapis.com/skydipper_materials/movie-tiles/MODIS/{z}/{x}/{y}.mp4" ],
      tiles: ['https://storage.googleapis.com/skydipper_materials/movie-tiles/EVI_TEST/{z}/{x}/{y}.mp4'],
      scheme: 'xyz',
      tileSize: 256,
      minzoom: 1,
      maxzoom: 4,
      playbackRate: 0.1
      // geometryFilter: 'https://storage.googleapis.com/deltares-video-map/mapbox-test/test1/geometry-filter.geojson'
    })
  }, render, {
    layers: [{
      id: id + '-raster',
      type: 'raster',
      source: id
    }]
  });

  return new CancelablePromise(function (resolve, reject) {
    if (layer) {
      resolve(layer);
    } else {
      reject(new Error('error in layer config'));
    }
  });
};

/* eslint-disable no-underscore-dangle */

var PluginMapboxGL = function () {
  function PluginMapboxGL(map, options) {
    var _this = this;

    classCallCheck(this, PluginMapboxGL);
    this.type = {
      raster: RasterLayer,
      vector: VectorLayer,
      geojson: GeoJsonLayer,
      video: VideoLayer
    };

    this.map = map;
    this.options = options;

    // You can change mapStyles and all the layers will be repositioned
    this.map.on('style.load', function () {
      var layers = _this.getLayers();

      layers.forEach(function (layer) {
        return _this.add(layer);
      });
    });
  }

  createClass(PluginMapboxGL, [{
    key: 'add',


    /**
     * Add a layer
     * @param {Object} layerModel
     */
    value: function add(layerModel) {
      var _this2 = this;

      var images = layerModel.images,
          mapLayer = layerModel.mapLayer;

      var allLayers = this.getLayers();

      if (Array.isArray(images)) {
        images.forEach(function (_ref) {
          var id = _ref.id,
              src = _ref.src,
              options = _ref.options;

          if (!_this2.map.hasImage(id)) {
            var img = new Image();
            img.src = src;
            img.onload = function () {
              _this2.map.addImage(id, img, options);
            };
          }
        });
      }

      // add source if it has one
      if (this.map && mapLayer && mapLayer.source && mapLayer.id && !this.map.getSource(mapLayer.id)) {
        this.map.addSource(mapLayer.id, mapLayer.source);
      }

      // add layers
      if (mapLayer && mapLayer.layers) {
        mapLayer.layers.forEach(function (l) {
          var _l$metadata = l.metadata,
              metadata = _l$metadata === undefined ? {} : _l$metadata;

          var nextLayerId = metadata.position === 'top' ? null : _this2.getNextLayerId(layerModel);

          if (_this2.map && !_this2.map.getLayer(l.id)) {
            _this2.map.addLayer(l, nextLayerId);
          }

          allLayers.forEach(function () {
            _this2.setZIndex();
          });
        });
      }
    }

    /**
     * Remove a layer
     * @param {Object} layerModel
     */

  }, {
    key: 'remove',
    value: function remove() {
      var _this3 = this;

      var layerModel = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var mapLayer = layerModel.mapLayer;

      if (mapLayer && mapLayer.layers && this.map && this.map.style) {
        mapLayer.layers.forEach(function (l) {
          if (_this3.map.getLayer(l.id)) {
            _this3.map.removeLayer(l.id);
          }
        });
      }

      if (mapLayer && !!this.map && this.map.getSource(mapLayer.id)) {
        this.map.removeSource(mapLayer.id);
      }
    }

    /**
     * Get method by type
     * @param {String} type
     */

  }, {
    key: 'getLayerByType',
    value: function getLayerByType(type) {
      return this.type[type];
    }

    /**
     * Get all mapbox layers
     */

  }, {
    key: 'getLayersOnMap',
    value: function getLayersOnMap() {
      return this.map.getStyle().layers;
    }
  }, {
    key: 'getLayer',
    value: function getLayer(layerModel) {
      var mapLayer = layerModel.mapLayer;


      if (this.map) {
        return this.map.getSource(mapLayer.id);
      }

      return null;
    }

    /**
     * Get all layers passed to layer manager
     */

  }, {
    key: 'getLayers',
    value: function getLayers() {
      var getLayers = this.options.getLayers;

      var layers = getLayers();
      return sortBy(layers, function (l) {
        return l.decodeFunction;
      });
    }

    /**
     * Get the layer above the given z-index
     * @param {Object} layerModel
     */

  }, {
    key: 'getNextLayerId',
    value: function getNextLayerId(layerModel) {
      var zIndex = layerModel.zIndex;

      var allLayers = this.getLayers();
      var layersOnMap = this.getLayersOnMap();

      // find the top layer for placing data layers below
      var customLayer = layersOnMap && layersOnMap.length && layersOnMap.find(function (l) {
        return l.id.includes('custom-layers') || l.id.includes('label') || l.id.includes('place') || l.id.includes('poi');
      });

      // make sure layers are sorted by zIndex
      var sortedLayers = sortBy(allLayers, function (l) {
        return l.zIndex;
      });

      // get the layer with zIndex greater than current layer from all layers
      var nextLayer = sortedLayers.find(function (l) {
        return l.zIndex > zIndex && (!l.mapLayer || !l.mapLayer.layers || !l.mapLayer.layers[0] || !l.mapLayer.layers[0].metadata || !l.mapLayer.layers[0].metadata.position);
      });

      // if no layer above it then use the custom layer
      if (!nextLayer || !!nextLayer && !nextLayer.mapLayer) {
        return customLayer.id;
      }

      // get the first layer of the next layer's array
      var nextLayerMapLayers = nextLayer.mapLayer.layers;
      var nextLayerMapLayer = nextLayerMapLayers[0];

      // Filter layers with custom metadata position
      var id = nextLayerMapLayer.id;

      // if it has a layer above it, check if that layer has been added to the map and get its id

      var isNextLayerOnMap = !!layersOnMap.find(function (l) {
        return id === l.id;
      });

      // if next layer is on map return the id, else return the custom layer to add below
      return isNextLayerOnMap ? id : customLayer.id;
    }

    /**
     * A namespace to set z-index
     * @param {Object} layerModel
     */

  }, {
    key: 'setZIndex',
    value: function setZIndex() {
      var _this4 = this;

      var allLayers = this.getLayers();
      var layersOnMap = this.getLayersOnMap();

      // set zIndex for all layers currently on map
      layersOnMap.forEach(function (l) {
        var id = l.id,
            _l$metadata2 = l.metadata,
            metadata = _l$metadata2 === undefined ? {} : _l$metadata2;

        var layerModel = allLayers.find(function (ly) {
          return id.includes(ly.id);
        });

        if (layerModel) {
          var nextLayerId = metadata.position === 'top' ? null : _this4.getNextLayerId(layerModel);
          _this4.map.moveLayer(id, nextLayerId);
        }
      });

      // set for all decode layers that don't exist inside mapStyle()
      var decodeLayers = allLayers.filter(function (l) {
        return !!l.decodeFunction;
      });

      if (decodeLayers && this.map) {
        decodeLayers.forEach(function (layerModel) {
          var mapLayer = layerModel.mapLayer;


          if (mapLayer) {
            var layers = mapLayer.layers;

            var parentLayer = layers[0];
            var childLayer = layers[1];

            var parentLayerOnMap = layersOnMap.find(function (ly) {
              return ly.id === parentLayer.id;
            });
            var childLayerOnMap = _this4.map.getLayer(childLayer.id);

            if (parentLayerOnMap && childLayerOnMap) {
              _this4.map.moveLayer(childLayer.id, parentLayer.id);
            }
          }
        });
      }

      return true;
    }

    /**
     * Given a desired value to give to a paint property of a layer, this function will return the
     * correct value to set, by taking into account other factors such as the opacity of the layer
     * @param {object} layerModel LayerModel instance
     * @param {string} layerId ID of the layer that contains the property
     * @param {string} propertyName Name of the property to set
     * @param {any} desiredValue Value to give the property
     */
    // eslint-disable-next-line class-methods-use-this

  }, {
    key: 'computePaintPropertyValue',
    value: function computePaintPropertyValue(layerModel, layerId, propertyName, desiredValue) {
      var opacity = layerModel.opacity,
          _layerModel$render$la = layerModel.render.layers,
          layers = _layerModel$render$la === undefined ? [] : _layerModel$render$la,
          mapLayer = layerModel.mapLayer;

      var isOpacityProperty = /-opacity$/.test(propertyName);

      // The opacity of the layer isn't relevant for this property
      if (!isOpacityProperty) {
        return desiredValue;
      }

      var _ref2 = layers.find(function (layer, index) {
        if (layer.id === undefined || layer.id === null) {
          return mapLayer.id + '-' + layer.type + '-' + index === layerId;
        }

        return layer.id === layerId;
      }) || {},
          _ref2$paint = _ref2.paint,
          paint = _ref2$paint === undefined ? {} : _ref2$paint;

      var currentValue = paint[propertyName];

      var res = 0.99 * opacity;

      if (currentValue !== undefined && currentValue !== null) {
        if (typeof currentValue === 'number') {
          res = currentValue * opacity * 0.99;
        } else if (Array.isArray(currentValue)) {
          res = currentValue.map(function (element) {
            return typeof element === 'number' ? element * opacity * 0.99 : element;
          });
        }
      }

      return res;
    }

    /**
     * A namespace to set opacity
     * @param {Object} layerModel
     * @param {Number} opacity
     */

  }, {
    key: 'setOpacity',
    value: function setOpacity(layerModel, opacity) {
      var _this5 = this;

      var PAINT_STYLE_NAMES = {
        symbol: ['icon', 'text'],
        circle: ['circle', 'circle-stroke']
      };

      var mapLayer = layerModel.mapLayer,
          decodeFunction = layerModel.decodeFunction;


      if (mapLayer.layers && !decodeFunction) {
        mapLayer.layers.forEach(function (l) {
          // Select the style to change depending on the type of layer
          var paintStyleNames = PAINT_STYLE_NAMES[l.type] || [l.type];

          // Loop each style name and check if there is an opacity in the original layer
          paintStyleNames.forEach(function (name) {
            var propertyName = name + '-opacity';
            var propertyValue = _this5.computePaintPropertyValue(layerModel, l.id, propertyName, opacity);

            _this5.map.setPaintProperty(l.id, propertyName, propertyValue);
          });
        });
      }

      if (decodeFunction) {
        var layer = mapLayer.layers[1];

        if (layer && typeof layer.setProps === 'function') {
          layer.setProps({ opacity: opacity });
        }
      }
    }

    /**
     * A namespace to hide or show a selected layer
     * @param {Object} layerModel
     * @param {Boolean} visibility
     */

  }, {
    key: 'setVisibility',
    value: function setVisibility(layerModel, visibility) {
      var _this6 = this;

      var mapLayer = layerModel.mapLayer;


      if (mapLayer && mapLayer.layers && this.map && this.map.style) {
        mapLayer.layers.forEach(function (l) {
          _this6.map.setLayoutProperty(l.id, 'visibility', visibility ? 'visible' : 'none');
        });
      }
    }
  }, {
    key: 'setSource',
    value: function setSource(layerModel) {
      var id = layerModel.id,
          source = layerModel.source;
      var type = source.type,
          data = source.data;


      if (this.map && type === 'geojson' && !!data && typeof data !== 'string') {
        var src = this.map.getSource(id);
        src.setData(data);
      }
      return this;
    }
  }, {
    key: 'setRender',
    value: function setRender(layerModel) {
      var _this7 = this;

      var mapLayer = layerModel.mapLayer,
          render = layerModel.render;
      var _render$layers = render.layers,
          renderLayers = _render$layers === undefined ? [] : _render$layers;


      if (!mapLayer || !renderLayers.length) {
        return this;
      }

      try {
        mapLayer.layers.forEach(function (layer, i) {
          var id = layer.id;

          var rl = renderLayers[i] || renderLayers[0] || {}; // take the style for each layer or use the first one for all of them
          var _rl$paint = rl.paint,
              paint = _rl$paint === undefined ? {} : _rl$paint,
              _rl$layout = rl.layout,
              layout = _rl$layout === undefined ? {} : _rl$layout,
              _rl$filter = rl.filter,
              filter = _rl$filter === undefined ? null : _rl$filter;


          _this7.map.setFilter(id, filter);

          Object.keys(paint).forEach(function (p) {
            _this7.map.setPaintProperty(id, p, _this7.computePaintPropertyValue(layerModel, id, p, paint[p]));
          });

          Object.keys(layout).forEach(function (l) {
            _this7.map.setLayoutProperty(id, l, layout[l]);
          });
        });
      } catch (error) {
        console.error(error);
      }

      // NOT COMPATIBLE WITH REACT MAP GL
      // if (true) {
      //   document.querySelector('.overlays').style = 'pointer-events: none;'

      //   mapLayer.layers.forEach((layer) => {
      //     const { id, source, 'source-layer': sourceLayer } = layer;

      //     // When the user moves their mouse over the state-fill layer, we'll update the
      //     // feature state for the feature under the mouse.
      //     this.map.off('mousemove', id);
      //     this.map.on('mousemove', id, (e) => {
      //       const { hoverId } = layerModel;

      //       if (e.features.length > 0) {
      //         layerModel.set('hoverId', e.features[0].id);

      //         if (hoverId) {
      //           this.map.setFeatureState({
      //             source,
      //             ...sourceLayer && { sourceLayer },
      //             id: hoverId
      //           }, { hover: false });
      //         }

      //         this.map.setFeatureState({
      //           source,
      //           ...sourceLayer && { sourceLayer },
      //           id: e.features[0].id
      //         }, { hover: true });
      //       }
      //     });

      //     // When the mouse leaves the state-fill layer, update the feature state of the
      //     // previously hovered feature.
      //     this.map.off('mouseleave', id);
      //     this.map.on('mouseleave', id, () => {
      //       const { hoverId } = layerModel;

      //       if (hoverId) {
      //         this.map.setFeatureState({
      //           source,
      //           ...layer['source-layer'] && { 'sourceLayer': layer['source-layer'] },
      //           id: hoverId
      //         }, { hover: false });
      //       }
      //       layerModel.set('hoverId', null);
      //     });
      //   })
      // }
      return this;
    }
  }, {
    key: 'setParams',
    value: function setParams() {
      return this;
    }
  }, {
    key: 'setSQLParams',
    value: function setSQLParams() {
      return this;
    }
  }, {
    key: 'setDecodeParams',
    value: function setDecodeParams(layerModel) {
      var mapLayer = layerModel.mapLayer,
          decodeParams = layerModel.decodeParams;


      if (!mapLayer) {
        return this;
      }

      var layer = mapLayer.layers[1];

      if (layer && typeof layer.setProps === 'function') {
        layer.setProps({ decodeParams: decodeParams });
      } else {
        console.error("Layer is not present. You defined decodeParams but maybe you didn't define a decodeFunction");
      }

      return this;
    }
  }, {
    key: 'unmount',
    value: function unmount() {
      this.map = null;
    }
  }]);
  return PluginMapboxGL;
}();

export default LayerManager;
export { PluginMapboxGL as PluginMapboxGl, replace, substitution, concatenation, fetch$1 as fetch };
